import { useState } from "react";
import { BUILD_INFO } from "@/build-info";

/**
 * Tiny floating badge showing the current build's commit SHA + build time.
 * Click to expand and see the full info.
 *
 * Purpose: verify at a glance which build is running in production vs preview.
 * If prod shows an older SHA than preview → the deploy didn't push the latest.
 */
export default function BuildBadge() {
  const [open, setOpen] = useState(false);

  const shortSha = BUILD_INFO?.commitShort || "unknown";
  const buildTime = BUILD_INFO?.buildTime || "";
  const built = buildTime ? new Date(buildTime) : null;
  const builtLabel = built
    ? built.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  return (
    <div
      data-testid="build-badge"
      style={{
        position: "fixed",
        bottom: 10,
        right: 10,
        zIndex: 9999,
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
        fontSize: 11,
        lineHeight: 1.25,
        color: "rgba(255,255,255,0.75)",
        background: "rgba(15,15,20,0.7)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 8,
        padding: open ? "8px 10px" : "4px 8px",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        cursor: "pointer",
        userSelect: "none",
        maxWidth: open ? 320 : 180,
        transition: "all 160ms ease",
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
      }}
      onClick={() => setOpen((v) => !v)}
      title="Click for full build info"
    >
      {!open ? (
        <span data-testid="build-badge-compact">
          <span style={{ opacity: 0.55 }}>build</span>{" "}
          <span style={{ color: "#7dd3fc" }}>{shortSha}</span>
          <span style={{ opacity: 0.45 }}> · {builtLabel}</span>
        </span>
      ) : (
        <div data-testid="build-badge-expanded" style={{ whiteSpace: "pre-wrap" }}>
          <div><b>commit</b> {BUILD_INFO?.commit || "unknown"}</div>
          <div><b>branch</b> {BUILD_INFO?.branch || "—"}</div>
          <div><b>commit date</b> {BUILD_INFO?.commitDate || "—"}</div>
          <div><b>built</b> {buildTime || "—"}</div>
          <div style={{ opacity: 0.5, marginTop: 4 }}>
            also at <code>/version.json</code>
          </div>
        </div>
      )}
    </div>
  );
}
